import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { secureStorage, SessionManager, csrfToken } from '../utils/security';
import { getRoleBasedDashboard } from '../utils/roleRedirect';
import api from '../utils/api';
import notificationApiService from '../services/notificationApiService';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to subscribe to Pusher channel with retry logic
const subscribeToPusherChannel = async (userId, retries = 3) => {
  if (!userId) {
    return false;
  }

  // IMPORTANT: Check if user is still authenticated before subscribing
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const hasToken = !!localStorage.getItem('token');

  if (!isAuthenticated || !hasToken) {
    return false;
  }


  for (let attempt = 1; attempt <= retries; attempt++) {
    try {

      
      const laravelEchoModule = await import('../services/laravelEcho');

      // Call initializeLaravelEcho directly from the module
      const echo = laravelEchoModule.initializeLaravelEcho();

      if (!echo) {
        // Wait before retry
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        return false;
      }


      // IMPORTANT: Wait for Pusher connection BEFORE subscribing to channels
      const connected = await laravelEchoModule.waitForConnection();

      if (!connected) {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        return false;
      }

      // Subscribe to user-specific private channel
      // Laravel Echo expects channel name without 'private-' prefix for private() method
      const channelName = `App.User.${userId}`;
      // Subscribe to private channel with authentication (Echo adds 'private-' prefix)
      // This will trigger /broadcasting/auth with the valid socket_id
      const channel = echo.private(channelName);

      // Add subscription success/error handlers
      channel.subscribed(() => {
      });

      channel.error((error) => {
      });

      await new Promise(resolve => setTimeout(resolve, 1000));
      // Remove any existing listeners first
      channel.stopListening('TestPROD');
      channel.stopListening('.TestPROD');

      // Add listener for TestPROD event (with and without dot prefix)
      channel.listen('TestPROD', (data) => {

        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const notification = new Notification(data.title || 'Notification', {
              body: data.body || data.message || 'New notification',
              icon: '/favicon.ico',
              tag: 'notification-' + Date.now()
            });

            notification.onclick = () => {
              window.focus();
              notification.close();
            };
          } catch (err) {
          }
        }
      });

      channel.listen('.TestPROD', (data) => {

        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const notification = new Notification(data.title || 'Notification', {
              body: data.body || data.message || 'New notification',
              icon: '/favicon.ico',
              tag: 'notification-' + Date.now()
            });

            notification.onclick = () => {
              window.focus();
              notification.close();
            };
          } catch (err) {
          }
        }
      });

      // Add catch-all listener to see ALL events
      if (channel.listenForWhisper) {
        // Laravel Echo doesn't have a native "listen to all" but we can add common events
        const commonEvents = ['notification', '.notification', 'NotificationCreated', '.NotificationCreated',
                              'dataroom-invite', '.dataroom-invite', 'file-shared', '.file-shared'];

        commonEvents.forEach(eventName => {
          channel.listen(eventName, (data) => {
          });
        });
      }

      const isSubscribed = await laravelEchoModule.waitForChannelSubscription(`private-${channelName}`, 3000);

      if (isSubscribed) {
        return true;
      } else {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
      }
    } catch (error) {
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        continue;
      }
    }
  }
  return false;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage to prevent flash
  const [user, setUser] = useState(() => {
    const savedUserStr = localStorage.getItem('user');
    try {
      return savedUserStr ? JSON.parse(savedUserStr) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true' &&
           localStorage.getItem('token') !== null;
  });
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [isImpersonationLoading, setIsImpersonationLoading] = useState(false); // Loading state for impersonation
  const [sessionManager] = useState(new SessionManager(30)); // 30 minute timeout

  // Persistent Pusher subscription interval
  const [pusherSubscriptionInterval, setPusherSubscriptionInterval] = useState(null);
  
  // Impersonation state
  const [originalUser, setOriginalUser] = useState(() => {
    const savedOriginalUserStr = localStorage.getItem('originalUser');
    try {
      return savedOriginalUserStr ? JSON.parse(savedOriginalUserStr) : null;
    } catch {
      return null;
    }
  });
  const [originalToken, setOriginalToken] = useState(() => {
    return localStorage.getItem('originalToken') || null;
  });
  const [isImpersonating, setIsImpersonating] = useState(() => {
    return localStorage.getItem('isImpersonating') === 'true';
  });

  // Persistent Pusher subscription function - runs every 1 minute
  const startPersistentPusherSubscription = (userId) => {

    // Clear any existing interval
    if (pusherSubscriptionInterval) {
      clearInterval(pusherSubscriptionInterval);
    }

    // Function to ensure subscription
    const ensureSubscription = async () => {
      try {
        // IMPORTANT: Check if user is still authenticated before making API calls
        const isStillAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
        const hasToken = !!localStorage.getItem('token');

        if (!isStillAuthenticated || !hasToken) {
          stopPersistentPusherSubscription();
          return;
        }

        // Check if connection is already established - don't reconnect if already connected
        const laravelEchoModule = await import('../services/laravelEcho');
        const echo = laravelEchoModule.getEchoInstance();

        if (echo && echo.connector?.pusher) {
          const state = echo.connector.pusher.connection.state;
          // If already connected, no need to do anything
          if (state === 'connected') {
            return;
          }
        }

        // Only attempt reconnection if disconnected
        // Fetch fresh Pusher config from backend API
        try {
          const configService = await import('../services/configService');
          const pusherConfig = await configService.default.getPusherConfig();

          if (pusherConfig && pusherConfig.PUSHER_APP_KEY) {
            localStorage.setItem('pusher_config', JSON.stringify(pusherConfig));
          }
        } catch (error) {
        }

        // Attempt subscription only if not already connected
        const subscribed = await subscribeToPusherChannel(userId, 1); // 1 retry only for periodic checks
        if (subscribed) {
        } else {
        }

        // Also ensure Pusher service is initialized only if not connected
        const pusherService = await import('../services/pusherService');
        if (pusherService.default && !pusherService.default.isConnected) {
          await pusherService.default.initialize(userId);
        }
      } catch (error) {
      }
    };

    // Run immediately on start
    ensureSubscription();

    // Then run every 60 seconds
    const intervalId = setInterval(ensureSubscription, 60000); // 60 seconds
    setPusherSubscriptionInterval(intervalId);

    return intervalId;
  };

  // Stop persistent subscription
  const stopPersistentPusherSubscription = () => {
    if (pusherSubscriptionInterval) {
      clearInterval(pusherSubscriptionInterval);
      setPusherSubscriptionInterval(null);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');

      if (isAuthenticated && token && user) {
        // Set axios default header with plain token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Initialize CSRF token
        const csrf = csrfToken.generate();
        csrfToken.store(csrf);

        // Initialize real-time services immediately if user is already logged in
        // This handles page refreshes and app restarts
        if (user?.id) {

          (async () => {
            try {
              // Double-check authentication before fetching config
              const isStillAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
              const hasToken = !!localStorage.getItem('token');

              if (!isStillAuthenticated || !hasToken) {
                return;
              }

              // First, ensure Pusher config is loaded from backend API
              try {
                const configService = await import('../services/configService');
                const pusherConfig = await configService.default.getPusherConfig();

                if (pusherConfig && pusherConfig.PUSHER_APP_KEY) {
                  localStorage.setItem('pusher_config', JSON.stringify(pusherConfig));
                }
              } catch (configError) {
              }

              // Initialize Laravel Echo globally
              const laravelEcho = await import('../services/laravelEcho');
              const echoInstance = laravelEcho.initializeLaravelEcho();
              if (echoInstance) {
              }

              // Subscribe to user's private channel
              const subscribed = await subscribeToPusherChannel(user.id);
              if (subscribed) {
              } else {
              }

              // Initialize Pusher service
              const pusherService = await import('../services/pusherService');
              if (pusherService.default) {
                const initialized = await pusherService.default.initialize(user.id);
                if (initialized) {
                }
              }

              // Fetch unread notification count after page refresh
              try {
                const unreadCount = await notificationApiService.getUnreadCount();

                // Store in localStorage for immediate display
                if (unreadCount >= 0) {
                  localStorage.setItem('unreadNotificationCount', unreadCount.toString());
                  // Dispatch custom event to notify Layout component
                  window.dispatchEvent(new Event('unreadCountUpdated'));
                }
              } catch (error) {
              }

              // Start persistent Pusher subscription interval
              startPersistentPusherSubscription(user.id);
            } catch (error) {
            }
          })(); // Execute immediately without any delay
        }
        
        // Start session manager
        sessionManager.start(
          () => {
            toast.error('Session expired. Please login again.');
            logout();
          },
          () => {
            toast('Your session will expire in 5 minutes.', { icon: '⚠️' });
          }
        );
      } else if (!token) {
        // Clear auth state if no token
        setIsAuthenticated(false);
        setUser(null);
      }
      
      // Set loading to false after a minimal delay to prevent flash
      setTimeout(() => setIsLoading(false), 100);
    };

    checkAuth();
    
    return () => {
      sessionManager.stop();
    };
  }, []);

  const login = async (email, password) => {
    try {
      
      // Create FormData as the API expects
      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);
      
      const response = await api.post('/login', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });


      // Check if 2FA is required
      if (response.data && response.data['2FA_REQUIRED'] === 'TRUE') {
        
        // Store email for OTP verification
        sessionStorage.setItem('2fa_email', email);
        sessionStorage.setItem('2fa_pending', 'true');
        
        // Return 2FA required status
        return { success: false, requires2FA: true, email: email };
      }
      
      // Normal login flow (no 2FA required)
      if (response.data && response.data.token) {
        const { user, token } = response.data;
        
        // Debug: Log the user object structure
        
        // Store authentication data
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('token', token); // Store plain token
        localStorage.setItem('user', JSON.stringify(user));
        
        // Store user data in secure storage (but not the token)
        secureStorage.setItem('userEmail', user.email);
        secureStorage.setItem('user', user);
        
        // Set axios default header with plain token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Initialize CSRF token
        const csrf = csrfToken.generate();
        csrfToken.store(csrf);
        
        // Start session manager
        sessionManager.start(
          () => {
            toast.error('Session expired. Please login again.');
            logout();
          },
          () => {
            toast('Your session will expire in 5 minutes.', { icon: '⚠️' });
          }
        );
        
        setIsAuthenticated(true);
        setUser(user);

        // Initialize real-time services immediately after login - BLOCKING
        // This ensures all users get subscribed to their channels BEFORE dashboard loads

        try {
          // First, ensure Pusher config is loaded from backend API
          try {
            const configService = await import('../services/configService');
            const pusherConfig = await configService.default.getPusherConfig();


            if (pusherConfig && pusherConfig.PUSHER_APP_KEY) {
              const configString = JSON.stringify(pusherConfig);
              localStorage.setItem('pusher_config', configString);

              // Verify it was saved
              const savedConfig = localStorage.getItem('pusher_config');

              if (savedConfig) {
                const parsed = JSON.parse(savedConfig);
              }
            } else {
            }
          } catch (configError) {
            // Continue anyway - maybe config is already in localStorage
            const existingConfig = localStorage.getItem('pusher_config');
            if (existingConfig) {
            } else {
            }
          }

          // NOTE: We don't manually call /broadcasting/auth here because:
          // 1. Pusher requires a valid socket_id format (e.g., "123456.789012")
          // 2. socket_id comes from an active Pusher connection, can't be generated manually
          // 3. Pusher will automatically call /broadcasting/auth when subscribing to private channels
          // 4. The auth endpoint is configured in Echo/Pusher config (authEndpoint)

          // Subscribe to user's private channel - AWAIT to block until done
          // This will trigger Pusher to call /broadcasting/auth with the correct socket_id
          const subscribed = await subscribeToPusherChannel(user.id);
          if (subscribed) {
          } else {
          }

          // Initialize Pusher service for additional features - AWAIT to block
          const pusherService = await import('../services/pusherService');
          if (pusherService.default) {
            const initialized = await pusherService.default.initialize(user.id);
            if (initialized) {
            } else {
            }
          }
        } catch (error) {
          // Don't block login if Pusher initialization fails
        }
        
        // Fetch unread notification count after successful login
        try {
          const unreadCount = await notificationApiService.getUnreadCount();

          // Store in localStorage for immediate display
          if (unreadCount >= 0) {
            localStorage.setItem('unreadNotificationCount', unreadCount.toString());
            // Dispatch custom event to notify Layout component
            window.dispatchEvent(new Event('unreadCountUpdated'));
          }
        } catch (error) {
          // Don't block login if notification fetch fails
        }

        // Start persistent Pusher subscription interval
        startPersistentPusherSubscription(user.id);

        toast.success('Login successful!');
        return { success: true };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(message);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/signup', userData);

      if (response.data && response.data.user) {
        toast.success(response.data.message);
        // Store user info for verification pages
        sessionStorage.setItem('pendingUser', JSON.stringify(response.data.user));
        sessionStorage.setItem('verificationEmail', response.data.user.email);
        sessionStorage.setItem('verificationPhone', response.data.user.phone_no);
        return { success: true, redirect: '/verify-email', user: response.data.user };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    // Store token and user before clearing for API call and unsubscription
    const token = localStorage.getItem('token');
    const currentUser = user; // Store current user for unsubscription

    // Stop persistent Pusher subscription interval
    stopPersistentPusherSubscription();

    // Unsubscribe from Pusher channels before logout
    if (currentUser?.id) {
      try {

        // Disconnect from Pusher service first
        const pusherService = await import('../services/pusherService');
        if (pusherService.default) {
          pusherService.default.unsubscribeUserChannel();
          pusherService.default.disconnect();
        }

        // Then disconnect Laravel Echo
        const laravelEcho = await import('../services/laravelEcho');
        const echo = laravelEcho.default.getInstance();

        if (echo) {
          // Leave all channels
          const channelName = `App.User.${currentUser.id}`;
          echo.leave(channelName);

          // Disconnect the Echo instance entirely and reset it
          laravelEcho.default.disconnect();
        }
      } catch (error) {
        // Don't block logout if unsubscription fails
      }
    }
    
    // Immediately clear all storage and state
    secureStorage.clear();
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');

    // Clear Pusher config to prevent stale config usage
    localStorage.removeItem('pusher_config');

    // Clear notification count
    localStorage.removeItem('unreadNotificationCount');

    // Clear impersonation data
    localStorage.removeItem('originalUser');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('isImpersonating');
    
    delete axios.defaults.headers.common['Authorization'];
    
    // Stop session manager
    sessionManager.stop();
    
    // Reset all state - this will trigger immediate redirect via ProtectedRoute
    setIsAuthenticated(false);
    setUser(null);
    setIsImpersonating(false);
    setOriginalUser(null);
    setOriginalToken(null);
    setIsImpersonationLoading(false);
    
    toast.success('Logged out successfully');
    
    // Make API call in background (non-blocking)
    if (token) {
      api.post('/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(error => {
        // Don't show error to user since they're already logged out
      });
    }
  };

  const forgotPassword = async (email) => {
    try {
      const formData = new FormData();
      formData.append('email', email);
      
      const response = await api.post('/forgot-password', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data?.success) {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data?.message || 'Failed to send reset link');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset link. Please try again.';
      throw new Error(message);
    }
  };

  const resetPassword = async ({ email, token, password, passwordConfirmation }) => {
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('token', token);
      formData.append('password', password);
      formData.append('password_confirmation', passwordConfirmation);
      
      const response = await api.post('/reset-password', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data?.success) {
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data?.message || 'Failed to reset password');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password. Please try again.';
      throw new Error(message);
    }
  };

  const getDashboardUrl = () => {
    return user ? getRoleBasedDashboard(user) : '/dashboard';
  };

  // Verify 2FA OTP for login
  const verify2FA = async (email, otp) => {
    try {
      
      const formData = new FormData();
      formData.append('identifier', email);
      formData.append('type', 'email');
      formData.append('otp', otp);
      formData.append('flow', 'login');
      
      const response = await api.post('/verify-otp', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });


      // After successful 2FA verification, the response should contain user data and token
      if (response.data && response.data.token) {
        const { user, token } = response.data;
        
        // Debug: Log the user object structure
        
        // Store authentication data
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Store user data in secure storage
        secureStorage.setItem('userEmail', user.email);
        secureStorage.setItem('user', user);
        
        // Set axios default header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Initialize CSRF token
        const csrf = csrfToken.generate();
        csrfToken.store(csrf);
        
        // Start session manager
        sessionManager.start(
          () => {
            toast.error('Session expired. Please login again.');
            logout();
          },
          () => {
            toast('Your session will expire in 5 minutes.', { icon: '⚠️' });
          }
        );
        
        // Clear 2FA session data
        sessionStorage.removeItem('2fa_email');
        sessionStorage.removeItem('2fa_pending');
        
        setIsAuthenticated(true);
        setUser(user);

        // Subscribe to Pusher channel for this user after 2FA - BLOCKING
        // This ensures all users get subscribed to their channels BEFORE dashboard loads

        try {
          // First, ensure Pusher config is loaded from backend API
          try {
            const configService = await import('../services/configService');
            const pusherConfig = await configService.default.getPusherConfig();

            if (pusherConfig && pusherConfig.PUSHER_APP_KEY) {
              localStorage.setItem('pusher_config', JSON.stringify(pusherConfig));
            } else {
            }
          } catch (configError) {
            // Continue anyway - maybe config is already in localStorage
          }

          // NOTE: We don't manually call /broadcasting/auth here because:
          // 1. Pusher requires a valid socket_id format (e.g., "123456.789012")
          // 2. socket_id comes from an active Pusher connection, can't be generated manually
          // 3. Pusher will automatically call /broadcasting/auth when subscribing to private channels
          // 4. The auth endpoint is configured in Echo/Pusher config (authEndpoint)

          // Subscribe via Laravel Echo - AWAIT to block until done
          // This will trigger Pusher to call /broadcasting/auth with the correct socket_id
          const subscribed = await subscribeToPusherChannel(user.id);
          if (subscribed) {
          } else {
          }

          // Also initialize Pusher service - AWAIT to block
          const pusherService = await import('../services/pusherService');
          if (pusherService.default) {
            const initialized = await pusherService.default.initialize(user.id);
            if (initialized) {
            } else {
            }
          }
        } catch (error) {
          // Don't block login if Pusher initialization fails
        }
        
        // Fetch unread notification count after successful 2FA verification
        try {
          const unreadCount = await notificationApiService.getUnreadCount();

          // Store in localStorage for immediate display
          if (unreadCount >= 0) {
            localStorage.setItem('unreadNotificationCount', unreadCount.toString());
            // Dispatch custom event to notify Layout component
            window.dispatchEvent(new Event('unreadCountUpdated'));
          }
        } catch (error) {
          // Don't block login if notification fetch fails
        }

        // Start persistent Pusher subscription interval
        startPersistentPusherSubscription(user.id);

        toast.success('2FA verification successful!');
        return { success: true };
      } else {
        throw new Error('Invalid 2FA verification response');
      }
    } catch (error) {
      const message = error.response?.data?.message || '2FA verification failed. Please try again.';
      toast.error(message);
      throw error;
    }
  };

  // Login as another user (impersonation)
  const loginAs = async (targetUserId) => {
    setIsImpersonationLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await api.post(`/admin/users/${targetUserId}/login-as`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {

        // Handle different response formats from the API
        const impersonatedUser = response.data.user || response.data.data?.user || response.data;
        const impersonatedToken = response.data.token || response.data.data?.token || response.data.access_token;


        if (!impersonatedToken) {
          throw new Error('No token received from impersonation API');
        }

        // Keep the impersonated user's actual role
        // The system will show menu based on the user's actual role (user or super-admin)
        // Also include has_owned_dataroom flag from the root response if available
        const modifiedUser = {
          ...impersonatedUser,
          // Add has_owned_dataroom flag from response root level if it exists
          ...(response.data.has_owned_dataroom !== undefined && { has_owned_dataroom: response.data.has_owned_dataroom })
        };


        // Unsubscribe from current user's channels before switching
        if (user?.id) {
          try {
            const pusherService = await import('../services/pusherService');
            if (pusherService.default) {
              pusherService.default.unsubscribeUserChannel();
              pusherService.default.disconnect();
            }

            const laravelEcho = await import('../services/laravelEcho');
            const echo = laravelEcho.default.getInstance();
            if (echo) {
              const channelName = `App.User.${user.id}`;
              echo.leave(channelName);
              laravelEcho.default.disconnect();
            }
          } catch (error) {
          }
        }

        // Store original super admin credentials
        if (!isImpersonating) {
          // Make sure we're storing the actual super admin user with their roles
          localStorage.setItem('originalUser', JSON.stringify(user));
          localStorage.setItem('originalToken', token);
          setOriginalUser(user);
          setOriginalToken(token);
        }

        // Set impersonated user credentials with forced user role
        localStorage.setItem('user', JSON.stringify(modifiedUser));
        localStorage.setItem('token', impersonatedToken);
        localStorage.setItem('isImpersonating', 'true');
        
        // Update axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonatedToken}`;
        
        // Update state with modified user
        setUser(modifiedUser);
        setIsImpersonating(true);

        // Subscribe to impersonated user's channels - BLOCKING
        // This ensures the impersonated user is subscribed BEFORE redirect
        if (impersonatedUser.id) {

          try {
            // Subscribe to impersonated user's private channel - AWAIT to block
            const subscribed = await subscribeToPusherChannel(impersonatedUser.id);
            if (subscribed) {
            } else {
            }

            // Initialize Pusher service for impersonated user - AWAIT to block
            const pusherService = await import('../services/pusherService');
            if (pusherService.default) {
              const initialized = await pusherService.default.initialize(impersonatedUser.id);
              if (initialized) {
              } else {
              }
            }
          } catch (error) {
            // Don't block impersonation if Pusher fails
          }
        }

        toast.success(`Logged in as ${impersonatedUser.name || impersonatedUser.email}`);

        // Keep loading state active during redirect
        // It will be cleared when the new page loads
        return { success: true };
      }
    } catch (error) {
      setIsImpersonationLoading(false);
      const message = error.response?.data?.message || 'Failed to impersonate user';
      toast.error(message);
      throw error;
    }
  };

  // Stop impersonation and return to super admin
  const stopImpersonation = async () => {
    setIsImpersonationLoading(true);
    try {
      if (!isImpersonating || !originalToken || !originalUser) {
        toast.error('Not currently impersonating');
        return;
      }

      // Call API to return to admin - using the impersonated user's token
      const currentToken = localStorage.getItem('token');
      
      // Set the current token in axios headers for the API call
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      
      const response = await api.post('/users/return-to-admin', {}, {
        headers: { 
          Authorization: `Bearer ${currentToken}`,
          Accept: 'application/json'
        }
      });

      // Always use the stored original super admin user
      // The original user should have super-admin role
      let adminUser = originalUser;
      let adminToken = originalToken;
      
      
      // If API returns a new token, use that
      if (response.data) {
        if (response.data.token || response.data.access_token) {
          adminToken = response.data.token || response.data.access_token;
        }
        
        // If API returns user data, validate it has super-admin role
        if (response.data.user || response.data.data?.user) {
          const apiUser = response.data.user || response.data.data?.user;
          
          // Check if the API user has super-admin role
          const apiUserRoles = Array.isArray(apiUser.roles) ? apiUser.roles : 
                              (apiUser.role ? [apiUser.role] : []);
          
          if (apiUserRoles.includes('super-admin') || apiUserRoles.includes('superadmin')) {
            // API returned valid super-admin user, use it
            adminUser = apiUser;
          } else {
            // API user doesn't have super-admin role, use original
          }
        }
      }
      
      // Ensure the final user has super-admin role
      if (!adminUser.roles || (!Array.isArray(adminUser.roles) && !adminUser.role)) {
        adminUser = {
          ...adminUser,
          roles: ['super-admin'],
          role: 'super-admin'
        };
      }

      // Unsubscribe from impersonated user's channels before switching back
      if (user?.id) {
        try {
          const pusherService = await import('../services/pusherService');
          if (pusherService.default) {
            pusherService.default.unsubscribeUserChannel();
            pusherService.default.disconnect();
          }

          const laravelEcho = await import('../services/laravelEcho');
          const echo = laravelEcho.default.getInstance();
          if (echo) {
            const channelName = `App.User.${user.id}`;
            echo.leave(channelName);
            laravelEcho.default.disconnect();
          }
        } catch (error) {
        }
      }

      // Restore super admin credentials
      localStorage.setItem('user', JSON.stringify(adminUser));
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('originalUser');
      localStorage.removeItem('originalToken');
      localStorage.removeItem('isImpersonating');

      // Update axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;

      // Update state
      setUser(adminUser);
      setOriginalUser(null);
      setOriginalToken(null);
      setIsImpersonating(false);

      // Subscribe to admin user's channels - BLOCKING
      // This ensures admin is subscribed BEFORE redirect
      if (adminUser.id) {

        try {
          // Subscribe to admin user's private channel - AWAIT to block
          const subscribed = await subscribeToPusherChannel(adminUser.id);
          if (subscribed) {
          } else {
          }

          // Initialize Pusher service for admin user - AWAIT to block
          const pusherService = await import('../services/pusherService');
          if (pusherService.default) {
            const initialized = await pusherService.default.initialize(adminUser.id);
            if (initialized) {
            } else {
            }
          }
        } catch (error) {
          // Don't block return to admin if Pusher fails
        }
      }

      toast.success('Returned to Super Admin account');
      
      // Force a page reload to ensure menu and all components refresh with super admin privileges
      // Keep loading state active during redirect
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
      
      return { success: true };
    } catch (error) {
      setIsImpersonationLoading(false);
      const message = error.response?.data?.message || 'Failed to stop impersonation';
      toast.error(message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading,
      isImpersonationLoading,
      isImpersonating,
      originalUser, 
      login, 
      logout, 
      register, 
      forgotPassword,
      resetPassword,
      verify2FA,
      getDashboardUrl,
      loginAs,
      stopImpersonation 
    }}>
      {children}
    </AuthContext.Provider>
  );
};